/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */
/**
 * TemplateStyles
 *
 * @file TemplateStyles.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import { styled } from '@mui/material/styles';
import { IconButton, Typography } from '@mui/material';
import Icon from '../../icons/Icon';
import mixins from '../../../assets/styles/mixins.scss';

export const ContentTemplate = styled('div')({
  '&&': {
    display: 'flex',
    justifyItems: 'center',
    alignItems: 'center',
    boxSizing: 'border-box'
  }
});

export const TextContainer = styled('div')({
  '&&': {
    padding: '0 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    alignItems: 'flex-start',
    flex: 1,
    minWidth: 0,
    position: 'relative',
    boxSizing: 'border-box'
  }
});

export const TitleWrapper = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between'
  }
});

export const TitleText = styled(Typography)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flex: 1,
    minWidth: 0,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    lineHeight: '22px',
    fontWeight: mixins.fontWeight600
  }
});

export const TitleTextTypography = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    lineHeight: '22px',
    fontWeight: mixins.fontWeight600
  }
});

export const SectionsContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
    overflowX: 'auto',
    marginTop: 2
  }
});

export const SectionsLabel = styled(Typography)({
  '&&': {
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    display: 'flex',
    alignItems: 'center',
    lineHeight: '20px'
  }
});

export const SectionChip = styled('span')({
  '&&': {
    background: mixins.feedBackColor,
    borderRadius: 4,
    padding: '0 2px',
    display: 'flex',
    alignItems: 'center',
    marginRight: 2
  }
});

export const ChipContent = styled('span')({
  '&&': {
    padding: '0 4px 2px 4px',
    height: 20,
    gap: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

export const SectionNumber = styled('span')({
  '&&': {
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    lineHeight: '20px',
    fontWeight: mixins.fontWeight500,
    display: 'flex',
    alignItems: 'center'
  }
});

export const EditActionIcon = styled(Icon)({
  '&&': {
    color: mixins.color_text_primary,
    display: 'flex',
    fontSize: mixins.fontSize16,
    width: 16,
    height: 16,
    alignItems: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    lineHeight: '22px'
  }
});

export const EditActionIconButton = styled(IconButton)({
  '&&': {
    padding: 8,
    minWidth: 0,
    margin: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    textTransform: 'none',
    alignSelf: 'center',
    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '&:focus': {
      background: 'none'
    },
    '&:active': {
      background: 'none'
    },
    '&.editActionIcon': {
      visibility: 'hidden'
    },
    '&.editIconAlwaysVisible': {
      visibility: 'visible !important'
    }
  }
});

export const TitleTextContainer = styled('span')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    width: '100%'
  }
});

export const Container = styled('div')({
  '&&': {
    position: 'relative',
    width: '100%',
    flex: 1,
    minWidth: 0
  }
});

export const ErrorInfoIcon = styled('span')({
  '&&': {
    fontSize: 18,
    cursor: 'pointer'
  }
});

export const DateWarningIcon = styled('span')({
  '&&': {
    fontSize: 20,
    color: '#B45309',
    cursor: 'pointer'
  }
});

export const StatusColumnText = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    lineHeight: '20px',
    fontWeight: mixins.fontWeight400
  }
});

export const EditIconAlwaysVisible = styled('span')({
  '&&': {
    visibility: 'visible !important'
  }
});

export const EditActionIconContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  }
});

export const DateColumn = styled('div')({
  '&&': {
    display: 'flex',
    gap: 8
  }
});
